<script lang="ts" setup>
import StoryForm from '@/components/stories/StoryForm.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { Story } from '@/models/story.model';
import { useEvents, Event } from '@/services/events.composable';
import { Mutations, ShiftCMSState } from '@/store';
import {
  StoriesActions,
  StoriesMutations,
  StoriesState,
} from '@/stores/stories.store';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import TranslatedState from '@/components/common/TranslatedState.vue';
import { capitalize } from '@/util/string.util';
import ListFilter from '@/components/common/ListFilter.vue';
import { CategoriesActions } from '@/stores/categories.store';

const store = useStore<ShiftCMSState & { ts: StoriesState }>();
const router = useRouter();
const events = useEvents();
const stories = computed<Story[]>(() => store.state.ts.stories);

const addStory = () => {
  store.commit(Mutations.ShowDrawer, { component: markRaw(StoryForm) });
};

const showStory = (_: Event, { item }: { item: Story }) => {
  store.dispatch(StoriesActions.GetStory, item.id);

  router.push(`/stories/${item.id}`);
};

const update = () => {
  store.dispatch(StoriesActions.GetStories);
  store.dispatch(CategoriesActions.GetCategories);
};

onMounted(() => {
  events.on(Event.LanguageChanged, update);
  store.commit(StoriesMutations.SetStory, null);
  update();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, update);
  store.commit(Mutations.HideDrawer);
});

const headers = [
  { title: 'ID', key: 'id', width: '5%', sortable: true },
  { title: 'Title', key: 'title', width: '40%' },
  { title: 'Type', key: 'type', width: '5%' },
  { title: 'Category', key: 'mainCategory', width: '15%' },
  { title: 'Pages', key: 'items', width: '10%' },
  { title: 'State', key: 'translated', width: '25%', sortable: false },
];

const filter = ref<string>('');
const filterTitle = ref<string>('');
const filteredStories = computed(() =>
  stories.value
    .filter((v) => (filter.value ? v.type === filter.value : true))
    .filter((v) =>
      filterTitle.value
        ? v.title.toLowerCase().indexOf(filterTitle.value.toLowerCase()) !== -1
        : true,
    ),
);

function updateFilter(filters: { filter: string; filterTitle: string }) {
  filter.value = filters.filter;
  filterTitle.value = filters.filterTitle;
}
</script>
<template>
  <v-container>
    <h2>Stories <CurrentLanguage /></h2>
    <ListFilter
      :options="[
        { label: 'Quiz', value: 'quiz' },
        { label: 'Story', value: 'story' },
      ]"
      @filter="updateFilter"
    />
    <v-data-table
      density="compact"
      @click:row="showStory"
      :headers="headers"
      :items="filteredStories.sort((a, b) => a.index - b.index)"
      itemsPerPage="25"
      hover
    >
      <template v-slot:item.items="{ value }">
        {{ value.length }} pages
      </template>
      <template v-slot:item.type="{ value }">
        {{ capitalize(value) }}
      </template>
      <template v-slot:item.mainCategory="{ value }">
        {{ value?.title || 'Unknown' }}
      </template>
      <template v-slot:item.translated="{ item }">
        <TranslatedState :item="item" />
        &nbsp;
        <v-tooltip
          :text="
            item.published
              ? 'This story is published'
              : 'This story is not published'
          "
        >
          <template v-slot:activator="{ props }">
            <v-icon
              v-bind="props"
              :icon="item.published ? 'mdi-eye-check' : 'mdi-eye-outline'"
              :color="item.published ? 'green' : 'red'"
            ></v-icon>
          </template>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-btn @click="addStory" color="success">Add Story</v-btn>
  </v-container>
</template>
